import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootStore } from 'redux/store';
import TokenImage from 'assets/images/token-24.svg';
import { Grid, TableRow, TableHead, TableContainer, TableCell, TableBody, Table } from '@material-ui/core';
import CrownImage from 'assets/images/crown.svg';
import { Loader } from 'components/Loader';
import './Leaderboard.scss';
import { TeamUsers } from 'redux/models/teamUsers';
import { orderByProperty } from 'helpers';
import { Alert } from '@material-ui/lab';

const SMALL_LEADERBOARD = 3;
const LARGE_LEADERBOARD = 5;

const Leaderboard: React.FC = () => {
	const userState = useSelector((state: RootStore) => state.user);
	const teamMembers = userState.users;
	const [leaderboardMembers, setLeaderboardMembers] = useState<TeamUsers[]>([]);
	const [nonLeaderboardMembers, setNonLeaderboardMembers] = useState<TeamUsers[]>([]);
	const [leaderboardLength, setLeaderboardLength] = useState<number>(SMALL_LEADERBOARD);
	let place: number = 1;

	const showRemainingTeamMembers = () => {
		const memberRows = nonLeaderboardMembers?.map((member, index) => {
			const currentMember = member.userId === userState.userId;
			return (
				<React.Fragment key={leaderboardLength + 3 + index}>
					<TableRow key={leaderboardLength + 3 + index} className={`view-all-members-body-row ${currentMember ? 'current-user' : ''}`}>
						<TableCell component='th' scope='row' align='left' className='view-all-members-body-item'>
							<Grid container justifyContent='flex-start'></Grid>
						</TableCell>
						<TableCell align='left' className='view-all-members-body-item'>
							{member.userName}
						</TableCell>
						<TableCell align='left' className='view-all-members-body-item'>
							{(currentMember || userState.selectedTeam?.isAdmin) && (
								<div className='view-all-members-token-container'>
									<img src={TokenImage} height={14} width={14} className='view-all-members-token-icon' alt='token' />
									{member.tokens}
								</div>
							)}
						</TableCell>
					</TableRow>
				</React.Fragment>
			);
		});

		return (
			<React.Fragment>
				<TableRow key={leaderboardLength + 2} className='view-all-members-body-row non-leader-members-spacer'>
					<TableCell align='left' className='view-all-members-body-item non-leader-members-text' colSpan={3}>
						{`+ ${nonLeaderboardMembers.length} more team ${nonLeaderboardMembers.length === 1 ? 'member' : 'members'} (sorted alphabetically)`}
					</TableCell>
				</TableRow>
				{memberRows}
			</React.Fragment>
		);
	};

	useEffect(() => {
		if (teamMembers && teamMembers.length > 0) {
			let members;
			let nonLeaders;
			if (teamMembers.length > 10) {
				members = teamMembers.filter((member, index) => index < LARGE_LEADERBOARD);
				nonLeaders = teamMembers.filter((member, index) => index > LARGE_LEADERBOARD - 1);
				setLeaderboardLength(LARGE_LEADERBOARD);
			} else {
				members = teamMembers.filter((member, index) => index < SMALL_LEADERBOARD);
				nonLeaders = teamMembers.filter((member, index) => index > SMALL_LEADERBOARD - 1);
				setLeaderboardLength(SMALL_LEADERBOARD);
			}
			setLeaderboardMembers(members);
			nonLeaders = orderByProperty(nonLeaders, 'userName') as TeamUsers[];
			setNonLeaderboardMembers(nonLeaders);
		}
	}, [teamMembers, userState]);

	return (
		<>
			<Grid container direction='column' alignItems='center'>
				{userState.usersLoading ? (
					<Loader />
				) : !userState.selectedTeam?.hideLeaderboard ? (
					<>
						{userState?.selectedTeam?.isAdmin && (
							<Alert severity='info' className='leaderboard-admin-info'>
								As an 'Administrator' of this team you can see the tokens for all team members. Others are limited to only see the tokens of the
								members in the leaderboard.
							</Alert>
						)}
						<TableContainer className='view-all-members-table-container'>
							<div className='view-all-members-scroll-container'>
								<Table aria-label='simple table' className='view-all-members-table'>
									<TableHead className='view-all-members-header'>
										<TableRow className='view-all-members-header-row'>
											<TableCell className='view-all-members-header-item rank-table-header'>Rank</TableCell>
											<TableCell className='view-all-members-header-item name-table-header' align='left'>
												Name
											</TableCell>
											<TableCell className='view-all-members-header-item' align='left'>
												Tokens
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody className='view-all-members-body'>
										{leaderboardMembers.map((member, index) => {
											if (leaderboardMembers[index - 1] && leaderboardMembers[index - 1].tokens > leaderboardMembers[index].tokens) {
												place = place + 1;
											}

											return (
												<TableRow
													key={index}
													className={`view-all-members-body-row ${member.userId === userState.userId ? 'current-user' : ''}`}
												>
													<TableCell component='th' scope='row' align='left' className='view-all-members-body-item'>
														<Grid container justifyContent='flex-start'>
															{place === 1 ? <img src={CrownImage} alt='crown-icon' /> : place}
														</Grid>
													</TableCell>
													<TableCell align='left' className='view-all-members-body-item'>
														{member.userName}
													</TableCell>
													<TableCell align='left' className='view-all-members-body-item'>
														<div className='view-all-members-token-container'>
															<img src={TokenImage} height={14} width={14} className='view-all-members-token-icon' alt='token' />
															{member.tokens}
														</div>
													</TableCell>
												</TableRow>
											);
										})}
										{nonLeaderboardMembers && nonLeaderboardMembers.length > 0 && showRemainingTeamMembers()}
									</TableBody>
								</Table>
							</div>
						</TableContainer>
					</>
				) : (
					<div className='disabled-leaderboard'>The administrator of this team has disabled the leaderboard</div>
				)}
			</Grid>
		</>
	);
};

export default Leaderboard;
